import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useLazyQuery, useQuery } from '@apollo/client';
import { getContentRoute, getModuleRoute, getUnitRoute } from '../helpers';
import setLocalStorageItem from '../../../../../helpers/setLocalStorageItem';
// import Page404 from '../../../../../general-pages/Page404';
import {
  StudyGroup,
  Content,
  TrackUnit,
  TrackModule,
  TrackContent,
  Query,
  Track,
  Pinning,
  Share,
  ChalkUp,
  Program,
} from '../../../../../generated/graphqlModels';
import {
  GET_PROGRAM,
  GET_PROGRAM_AND_PROGRAM_UNITS,
  GET_PROGRAM_STUDY_GROUP,
  GET_PROGRAM_STUDY_GROUPS,
  GET_STUDY_GROUP,
  GET_STUDY_GROUP_WITHOUT_USERS,
  GET_TRACK,
  GET_TRACK_PROGRESS,
  USER_CHALKUPS_WITHOUT_CLASS,
} from '../queries';
import { useQueryParam } from '../../../../../hooks/useQueryParams';
import { useUpdateQueryParams } from '../../../../../helpers/updateQueryParams';

const LOCAL_STORAGE_KEY = 'unit-page-state';

const filterSearchParams = (params: string, whitelist: string[]) => {
  const urlParams = new URLSearchParams(params);

  urlParams.forEach((_, key) => {
    if (!whitelist.includes(key)) {
      urlParams.delete(key);
    }
  });

  return '?' + urlParams.toString();
};

function transformProgramWithProgramUnitsToTrack(program: any) {
  // Transform the program units into track units
  const trackUnits = program.programUnits.map((unit: any) => {
    return {
      ...unit,
      canBeShared: -1,
      id: unit.id, // Unit ID
      unit: {
        ...unit,
        id: unit.id,
        name: unit.name,
        isDemo: false, // Assuming isDemo is false
        isPublished: true,
        __typename: 'Unit',
      },
      conditionEnabled: true,
      conditionShown: true,
      trackModules: unit.modules.map((module: any) => {
        return {
          id: module.id, // Module ID
          conditionEnabled: true,
          conditionShown: true,
          module: {
            id: module.id,
            name: module.name,
          },
          trackContents: module.contents.map((content: any) => {
            return {
              id: content.id, // Content ID
              conditionEnabled: true,
              conditionShown: true,
              content: {
                id: content.id,
                name: content.name,
                mediaType: 'cms', // Assuming all content has mediaType "cms"
                isShareable: true, // Assuming isShareable is true
                isDownloadable: true, // Assuming isDownloadable is true
                isDemo: false, // Assuming isDemo is false
              },
            };
          }),
        };
      }),
    };
  });

  return {
    data: {
      track: {
        description: '',
        enabled: true,
        estimatedDuration: 1,
        program: program,
        id: program.id, // Track ID from program ID
        name: 'Default', // Default name for the track
        conditionEnabled: true,
        conditionShown: true,
        trackUnits: trackUnits,
        tags: ['global'],
        trackGuides: [],
      },
    },
  };
}

export interface State {
  trackId?: string;
  studyGroupId?: string;
  contentId?: string;
  program?: Program;
  studyGroup?: StudyGroup;
  studyGroups?: StudyGroup[];
  track?: Track;
  tracks?: Track[];
  trackUnit?: TrackUnit;
  trackModule?: TrackModule;
  trackContent?: TrackContent;
  pinnings?: Pinning[];
  shares?: Share[];
  chalkups?: ChalkUp[];
  trackUnitNotFound: boolean;
  trackModuleNotFound: boolean;
  trackContentNotFound: boolean;
  isTrackLoading: boolean;
  isStudyGroupLoading: boolean;
  isUnitLoading: boolean;
  isModuleLoading: boolean;
  isContentLoading: boolean;
  isChalkupsLoading: boolean;
  isPinningsLoading: boolean;
  isSharesLoading: boolean;
  isTeacherGuideDisplayed: boolean;
  isSaveAvailable: boolean;
  isSaving: boolean;
  isDemo: boolean;
  contentName?: string;
}

interface usePesistStateType {
  key: string;
  state: State;
  setState: (state: any) => void;
}

let lastStudyGroupId: string | null = null;

const multiPageCacheState = {
  trackUnitNotFound: false,
  trackModuleNotFound: false,
  trackContentNotFound: false,
  isTrackLoading: false,
  isStudyGroupLoading: false,
  isUnitLoading: false,
  isModuleLoading: false,
  isContentLoading: false,
  isChalkupsLoading: false,
  isPinningsLoading: false,
  isSharesLoading: false,
  isTeacherGuideDisplayed: true,
  isSaveAvailable: false,
  isSaving: false,
  isDemo: false,
};

let cachedMultiPageCacheState: State = { ...multiPageCacheState };

const usePersistState = ({ key, state, setState }: usePesistStateType) => {
  useEffect(() => {
    if (!key) {
      return;
    }
    const oldState = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (oldState)
      try {
        const grouptUnitData = JSON.parse(oldState)[key];
        setState({
          ...state,
          ...grouptUnitData,
          isSaving: false,
          isSaveAvailable: false,
        });
      } catch (e) {
        console.log(e);
      }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!key) {
      return;
    }
    const oldLocalStorageData = localStorage.getItem(LOCAL_STORAGE_KEY);
    let newLocalStorageData: any = {};
    if (oldLocalStorageData) {
      try {
        newLocalStorageData = JSON.parse(oldLocalStorageData) || {};
      } catch (e) {
        // Pass
      }
    }
    const stateToBeStored = {
      contentId: state.contentId,
      studyGroupId: state.studyGroupId,
      trackId: state.trackId,
    };
    newLocalStorageData[key] = JSON.parse(JSON.stringify(stateToBeStored));
    setLocalStorageItem(LOCAL_STORAGE_KEY, JSON.stringify(newLocalStorageData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, key]);
};

const useGetUnitDataGql = ({
  props,
  match,
  context,
  params,
  programId,
  id,
  isEdit,
  isChallenge,
  contentLevel,
}: any): [
  state: State,
  updateState: (_: Partial<State>) => void,
  additionalInfo: {
    contentLevel: string;
    unitToTrackUnit: Record<string, TrackUnit> | undefined;
    moduleToTrackUnit: Record<string, TrackUnit> | undefined;
    contentToTrackUnit: Record<string, TrackUnit> | undefined;
    contentToTrackModule: Record<string, TrackModule> | undefined;
    contentToTrackContent: Record<string, TrackContent> | undefined;
    goToNextResource: (replace?: boolean) => void;
    goToFirstResource: (replace?: boolean) => void;
    loading: boolean;
  },
] => {
  const qStudyGroupId = useQueryParam('study-group-id');
  const qIsSelfLearning = useQueryParam('is_self_learning');
  const isSelfLearning = qIsSelfLearning === 'true';
  const [state, setState] = useState<State>(
    qStudyGroupId === lastStudyGroupId
      ? cachedMultiPageCacheState
      : multiPageCacheState,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const updateQuery = useUpdateQueryParams();

  const { data: chalkupsWithoutClass } = useQuery(USER_CHALKUPS_WITHOUT_CLASS, {
    skip: !!qStudyGroupId || !context?.pk,
    variables: {
      userId: context?.pk,
    },
  });

  const changeQueryParamByKey = (
    newId: string | undefined | null,
    key = 'study-group-id',
  ) => {
    const queryParams = new URLSearchParams(location.search);
    if (newId) {
      queryParams.set(key, newId);
    } else {
      queryParams.delete(key);
    }
    updateQuery(queryParams, true);
  };

  const qTrackId = useQueryParam('track-id');
  let updateUrl = true;

  // const [key, setKey] = useState(programId + '-' + (id || ''));

  // usePersistState({ key, state, setState });

  const updateState = (newState: Partial<State>) => {
    setState((oldState) => {
      const updatedState = { ...oldState, ...newState };
      lastStudyGroupId = state.studyGroupId || null;
      cachedMultiPageCacheState = { ...updatedState };
      return updatedState;
    });
  };

  if (contentLevel === 'content' && state.contentId !== id) {
    updateState({ contentId: id });
  }

  const {
    loading: loadingProgram,
    error: errorProgram,
    data: dataProgram,
  } = useQuery<Query>(GET_PROGRAM_AND_PROGRAM_UNITS, {
    variables: { programId, classPage: contentLevel === 'program' },
    context: { studyGroupId: state.studyGroupId },
  });

  const [
    fetchStudyGroups,
    {
      loading: loadingProgramStudyGroups,
      data: dataProgramStudyGroups,
      error: programStudyGroupsError,
    },
  ] = useLazyQuery<Query>(GET_PROGRAM_STUDY_GROUPS, {
    variables: { programIdStr: programId },
    context: { studyGroupId: state.studyGroupId },
  });

  const {
    loading: loadingProgramStudyGroup,
    data: dataProgramStudyGroup,
    error: programStudyGroupError,
  } = useQuery<Query>(GET_PROGRAM_STUDY_GROUP, {
    variables: { programIdStr: programId, studyGroupId: qStudyGroupId },
    context: { studyGroupId: state.studyGroupId },
    fetchPolicy: 'network-only', // ENG-323
  });

  const [
    getTrack,
    { loading: loadingTrack, error: errorTrack, data: dataTrack },
  ] = useLazyQuery<Query>(GET_TRACK);

  const [getTrackProgress] = useLazyQuery<Query>(GET_TRACK_PROGRESS);

  const [
    getStudyGroup,
    {
      loading: loadingStudyGroup,
      error: errorStudyGroup,
      data: dataStudyGroup,
    },
  ] = useLazyQuery<Query>(
    isSelfLearning ? GET_STUDY_GROUP_WITHOUT_USERS : GET_STUDY_GROUP,
  );

  useEffect(() => {
    if (dataProgram) {
      let newState: Partial<State> = {};
      newState = { ...newState, program: dataProgram.program };
      updateState({
        ...newState,
      });
    }
  }, [dataProgram, qTrackId, qStudyGroupId]);

  useEffect(() => {
    if (
      !dataProgramStudyGroups &&
      !programStudyGroupsError &&
      (programStudyGroupError ||
        (dataProgramStudyGroup &&
          dataProgramStudyGroup.studyGroups.length === 0))
    ) {
      fetchStudyGroups();
    }
    const dataStudyGroups = dataProgramStudyGroups || dataProgramStudyGroup;
    if (dataStudyGroups) {
      let newState: Partial<State> = {};

      let sg: StudyGroup | undefined = undefined;

      if (dataStudyGroups.studyGroups.length === 1) {
        // @ts-ignore
        sg = dataStudyGroups.studyGroups[0];
      } else if (
        qStudyGroupId !== null &&
        dataStudyGroups.studyGroups &&
        dataStudyGroups.studyGroups.length > 0 &&
        dataStudyGroups.studyGroups.map((t) => t.id).includes(qStudyGroupId)
      ) {
        sg = dataStudyGroups.studyGroups.find((t) => t.id === qStudyGroupId);
      }
      updateUrl = false;
      updateState({
        ...newState,
        studyGroupId: sg?.id,
        studyGroups: dataStudyGroups.studyGroups,
      });
    }
  }, [
    dataProgramStudyGroups,
    dataProgramStudyGroup,
    programStudyGroupError,
    qTrackId,
    qStudyGroupId,
    programStudyGroupsError,
  ]);

  useEffect(() => {
    updateState({ isUnitLoading: loadingProgram || loadingTrack });
  }, [loadingProgram, loadingTrack]);

  useEffect(() => {
    updateState({ isUnitLoading: false, trackUnit: undefined });
  }, [state.track]);

  useEffect(() => {
    if (state.trackId) {
      updateState({ isContentLoading: true, isTrackLoading: true });
      getTrack({
        variables: {
          trackId: state.trackId,
          classPage: contentLevel === 'program',
        },
        context: {
          studyGroupId: state.studyGroupId,
        },
      }).then(() => {
        updateState({ isTrackLoading: false });
      });
      if (contentLevel === 'program') {
        getTrackProgress({
          variables: {
            trackId: state.trackId,
            trackIdString: state.trackId,
          },
          fetchPolicy: 'network-only',
          context: {
            studyGroupId: state.studyGroupId,
          },
        });
      }
    }
  }, [state.trackId, state.studyGroupId, contentLevel]);

  useEffect(() => {
    if (state.studyGroupId) {
      updateState({ isStudyGroupLoading: true });
      getStudyGroup({
        variables: { studyGroupId: state.studyGroupId, userId: context.pk },
      });
    }
  }, [state.studyGroupId]);

  useEffect(() => {
    let newState: Partial<State> = {};
    if (!state.studyGroupId && dataProgram) {
      const trackData = transformProgramWithProgramUnitsToTrack(
        (dataProgram as any).program,
      );
      const track = trackData.data.track;
      newState = {
        ...newState,
        trackId: track.id,
        tracks: [track],
        track: track,
      };

      updateState({
        ...newState,
        isStudyGroupLoading: false,
        isChalkupsLoading: false,
        isPinningsLoading: false,
        isSharesLoading: false,
        studyGroup: {
          // @ts-ignore
          chalkups: chalkupsWithoutClass?.chalkUps || [],
          // @ts-ignore
          shares: undefined,
          // @ts-ignore
        },
        chalkups: chalkupsWithoutClass?.chalkUps || [],
        shares: [],
        pinnings: [],
      });
    } else if (dataStudyGroup && dataStudyGroup.studyGroup) {
      const sg = dataStudyGroup.studyGroup;
      newState = { tracks: dataStudyGroup.studyGroup.tracksLicense };
      if (newState && newState.tracks && newState.tracks.length === 1) {
        // @ts-expect-error
        newState = { ...newState, trackId: newState.tracks[0].id };
      } else if (
        sg.trackChoice &&
        (sg.trackAssignments == null ||
          sg.trackAssignments.includes(sg.trackChoice))
      ) {
        newState = { ...newState, trackId: sg.trackChoice };
      } else if (sg.trackAssignments != null) {
        if (sg.trackAssignments.length === 1) {
          newState = { ...newState, trackId: sg.trackAssignments[0] };
        } else {
          if (newState.tracks)
            newState = {
              ...newState,
              tracks: newState.tracks.filter((t) =>
                sg.trackAssignments?.includes(t.id),
              ),
            };
        }
      }
      updateState({
        ...newState,
        isStudyGroupLoading: false,
        isChalkupsLoading: false,
        isPinningsLoading: false,
        isSharesLoading: false,
        studyGroup: {
          ...dataStudyGroup.studyGroup,
          // @ts-ignore
          chalkups: undefined,
          // @ts-ignore
          shares: undefined,
          // @ts-ignore
        },
        chalkups: dataStudyGroup.studyGroup.chalkups,
        shares: dataStudyGroup.studyGroup.shares,
        pinnings: dataStudyGroup.pinnings,
      });
    }
  }, [dataStudyGroup, errorStudyGroup, dataProgram, chalkupsWithoutClass]);

  const [
    unitToTrackUnit,
    moduleToTrackUnit,
    contentToTrackUnit,
    contentToTrackModule,
    contentToTrackContent,
  ] = useMemo(() => {
    const uTTU: Record<string, TrackUnit> = {};
    const mTTU: Record<string, TrackUnit> = {};
    const cTTU: Record<string, TrackUnit> = {};
    const cTTM: Record<string, TrackModule> = {};
    const cTTC: Record<string, TrackContent> = {};
    const track = dataTrack?.track || state.track;
    if (track) {
      for (const tu of track.trackUnits) {
        // @ts-ignore
        uTTU[tu.unit.id] = tu;
        if (tu.trackModules)
          for (const tm of tu.trackModules) {
            // @ts-ignore
            mTTU[tm.module.id] = tu;
            for (const tc of tm.trackContents) {
              // @ts-ignore
              cTTU[tc.content.id] = tu;
              // @ts-ignore
              cTTC[tc.content.id] = tc;
              // @ts-ignore
              cTTM[tc.content.id] = tm;
            }
          }
      }
      updateState({ isContentLoading: false, track: track });
    }
    return [uTTU, mTTU, cTTU, cTTM, cTTC];
  }, [dataTrack, state.trackId]);

  useEffect(() => {
    if (state.studyGroup && state.studyGroup.trackAssignments != null) {
      updateState({
        tracks: state.tracks?.filter((t) =>
          state.studyGroup?.trackAssignments?.includes(t.id),
        ),
      });
    }
  }, [state.studyGroup]);

  useEffect(() => {
    let newState: Partial<State> = {};
    if (contentLevel === 'unit') {
      if (unitToTrackUnit) {
        newState = {
          ...newState,
          trackUnit: unitToTrackUnit[id],
          trackModule: undefined,
          trackContent: undefined,
        };
      }
      newState = {
        ...newState,
        trackModule: undefined,
        trackContent: undefined,
      };
    } else if (contentLevel === 'module') {
      if (moduleToTrackUnit) {
        newState = { ...newState, trackUnit: moduleToTrackUnit[id] };
      }
      newState = {
        ...newState,
        trackModule: undefined,
        trackContent: undefined,
      };
    } else if (contentLevel === 'content') {
      if (contentToTrackUnit && Object.keys(contentToTrackUnit).length > 0) {
        if (contentToTrackUnit[id] == null) {
          newState = {
            ...newState,
            trackUnit: undefined,
            trackUnitNotFound: true,
          };
        } else {
          newState = {
            ...newState,
            trackUnit: contentToTrackUnit[id],
            trackUnitNotFound: false,
          };
        }
      }
      if (
        contentToTrackModule &&
        Object.keys(contentToTrackModule).length > 0
      ) {
        if (contentToTrackModule[id] == null) {
          newState = {
            ...newState,
            trackModuleNotFound: true,
            trackModule: undefined,
          };
        } else {
          newState = {
            ...newState,
            trackModuleNotFound: false,
            trackModule: contentToTrackModule[id],
          };
        }
      }
      if (
        contentToTrackContent &&
        Object.keys(contentToTrackContent).length > 0
      ) {
        if (contentToTrackContent[id] == null) {
          newState = {
            ...newState,
            trackContentNotFound: true,
            trackContent: undefined,
          };
        } else {
          newState = {
            ...newState,
            trackContentNotFound: false,
            trackContent: contentToTrackContent[id],
          };
        }
      }
    }
    if (Object.keys(newState).length > 0) {
      updateState(newState);
    }
  }, [
    dataTrack,
    unitToTrackUnit,
    moduleToTrackUnit,
    contentToTrackUnit,
    contentToTrackModule,
    contentToTrackContent,
    contentLevel,
    id,
  ]);

  useEffect(() => {
    if (state.trackId != null && qTrackId !== state.trackId) {
      changeQueryParamByKey(state.trackId, 'track-id');
    }
  }, [state.trackId]);

  useEffect(() => {
    if (state.studyGroupId != null && qStudyGroupId !== state.studyGroupId) {
      if (updateUrl && qStudyGroupId) {
        updateState({ studyGroupId: qStudyGroupId, studyGroup: undefined });
      } else {
        changeQueryParamByKey(state.studyGroupId, 'study-group-id');
      }
    }
  }, [state.studyGroupId]);

  // useEffect(() => {
  //   if (state.trackUnit && state.trackUnit.id) {
  //     setKey(programId + '-' + id);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [state]);

  const goToNextResource = (
    replace: boolean = false,
    isCurrentFound = false,
  ) => {
    let nextResource: Content | null = null;

    if (state.trackUnit && state.trackContent) {
      // eslint-disable-next-line no-unused-expressions
      state.trackUnit.trackModules.forEach((m) => {
        if (nextResource || !state.trackContent) {
          return;
        }
        m.trackContents.forEach((c) => {
          if (nextResource) {
            return;
          }
          if (
            isCurrentFound &&
            ((context.is_demo && c.content.isDemo) || !context.is_demo)
          ) {
            nextResource = c.content;
            // @ts-ignore
          } else if (c.content.id === state.trackContent.content.id) {
            isCurrentFound = true;
          }
        });
      });
    }
    if (nextResource !== null) {
      const searchParams = filterSearchParams(location.search, [
        'track-id',
        'study-group-id',
      ]);
      if (replace) {
        navigate(
          // @ts-ignore
          getContentRoute(programId, nextResource.id, searchParams),
          { replace: true },
        );
      } else {
        navigate(
          // @ts-ignore
          getContentRoute(programId, nextResource.id, searchParams),
        );
      }
    }
  };

  const goToFirstResource = (replace: boolean = false) => {
    let firstResource: Content | null = null;
    let newState: Partial<State> = {};
    const searchParams = filterSearchParams(location.search, [
      'track-id',
      'study-group-id',
      'is_explore',
    ]);
    if (contentLevel === 'unit') {
      if (state.trackUnit && state.trackUnit.unit.id === id) {
        if (state.trackUnit.trackModules) {
          // @ts-expect-error
          firstResource =
            state.trackUnit.trackModules[0]?.trackContents[0]?.content;
        }
      } else if (state.track) {
        // // @ts-expect-error
        // firstResource =
        //   state.track?.trackUnits[0]?.trackModules[0]?.trackContents[0]
        //     ?.content;
        // newState = { trackUnit: state.track.trackUnits[0] };
      }
    } else if (contentLevel === 'module' && state.trackModule) {
      // @ts-expect-error
      firstResource = state.trackModule?.trackContents[0]?.content;
    } else if (
      contentLevel === 'content' &&
      state.track &&
      state.trackUnitNotFound
    ) {
      // @ts-expect-error
      firstResource =
        state.track?.trackUnits[0]?.trackModules[0]?.trackContents[0]?.content;
    }
    if (firstResource) {
      if (replace) {
        navigate(getContentRoute(programId, firstResource.id, searchParams), {
          replace: true,
        });
      } else {
        navigate(getContentRoute(programId, firstResource.id, searchParams));
      }
    }
  };

  useEffect(() => {
    // This fixes when user goes to the URL of content that is not demo
    if (context?.is_demo) {
      const content = state.trackContent?.content;
      if (content && !content.isDemo) {
        goToNextResource(true, true);
      }
    }
  }, [context, state]);

  if (['unit', 'module'].includes(contentLevel)) {
    goToFirstResource(true);
  }

  return [
    state,
    updateState,
    {
      contentLevel,
      unitToTrackUnit,
      moduleToTrackUnit,
      contentToTrackUnit,
      contentToTrackModule,
      contentToTrackContent,
      goToNextResource,
      goToFirstResource,
      loading:
        loadingProgram ||
        loadingStudyGroup ||
        loadingTrack ||
        loadingProgramStudyGroups ||
        loadingProgramStudyGroup,
    },
  ];
};

export default useGetUnitDataGql;
